// src/pages/admin/PendingRequests.tsx
import { useEffect, useState } from 'react';
import { Check, X, Eye, Clock, Car, Edit2, Save } from 'lucide-react';
import { Listing } from '../../lib/supabase';
import {
  adminGetPendingListings,
  adminApproveListing,
  adminRejectListing,
  adminUpdateListing,
} from '../../lib/adminApi';
import { AdminLayout } from '../../components/AdminLayout';

export function PendingRequests() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  
  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Listing>>({});

  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadPendingListings();
  }, []);

  const loadPendingListings = async () => {
    try {
      const data = await adminGetPendingListings();
      setListings(data);
    } catch (error) {
      console.error('Error loading pending listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    // If we are editing, save first, then approve? Or just block.
    if(isEditing) {
        alert("Veuillez enregistrer les modifications avant d'approuver.");
        return;
    }

    if (!confirm('Êtes-vous sûr de vouloir approuver cette annonce?')) return;

    setActionLoading(true);
    try {
      await adminApproveListing(id);

      setListings(listings.filter(listing => listing.id !== id));
      setSelectedListing(null);
      alert('Annonce approuvée avec succès!');
    } catch (error) {
      console.error('Error approving listing:', error);
      alert('Une erreur est survenue');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir rejeter cette annonce? Elle sera supprimée définitivement.')) return;

    setActionLoading(true);
    try {
      await adminRejectListing(id);

      setListings(listings.filter(listing => listing.id !== id));
      setSelectedListing(null);
      alert('Annonce rejetée et supprimée');
    } catch (error) {
      console.error('Error rejecting listing:', error);
      alert('Une erreur est survenue');
    } finally {
      setActionLoading(false);
    }
  };

  const openModal = (listing: Listing) => {
    setSelectedListing(listing);
    setEditFormData(listing);
    setIsEditing(false);
  };

  const handleUpdate = async () => {
    if (!selectedListing || !editFormData) return;
    setActionLoading(true);
    try {
        await adminUpdateListing(selectedListing.id, {
            brand: editFormData.brand,
            model: editFormData.model,
            price: editFormData.price,
            year: editFormData.year,
            mileage: editFormData.mileage,
            description: editFormData.description,
            contact_phone: editFormData.contact_phone,
            location: editFormData.location,
        });

        // Update local state
        const updatedListing = { ...selectedListing, ...editFormData } as Listing;
        setListings(listings.map(l => l.id === selectedListing.id ? updatedListing : l));
        setSelectedListing(updatedListing);
        setIsEditing(false);
        alert('Modifications enregistrées. Vous pouvez maintenant approuver l\'annonce.');
    } catch (err) {
        console.error(err);
        alert('Erreur lors de la sauvegarde');
    } finally {
        setActionLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'price' || name === 'year' || name === 'mileage' ? Number(value) : value
    }));
  };

  return (
    <AdminLayout currentPage="pending">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Demandes en Attente</h2>
        <p className="text-sm text-gray-600">
          {loading ? 'Chargement...' : `${listings.length} demande${listings.length !== 1 ? 's' : ''} en attente`}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
             {/* Skeletons */}
             {[1, 2, 3].map((i) => (<div key={i} className="bg-white rounded-lg p-4 animate-pulse h-32"></div>))}
        </div>
      ) : listings.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg text-gray-600">Aucune demande en attente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map((listing) => (
            <div key={listing.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start space-x-3">
                <div className="w-24 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                  {listing.images && listing.images.length > 0 ? (
                    <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400"><Car /></div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{listing.brand} {listing.model}</h3>
                      <p className="text-xl font-bold text-[#D4452C] mt-1">{listing.price.toLocaleString()} TND</p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                      <Clock className="h-3 w-3 mr-1" /> En attente
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openModal(listing)}
                      className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      <Eye className="h-3 w-3" />
                      <span>Détails / Modifier</span>
                    </button>
                    {/* Quick actions */}
                    <button
                      onClick={() => handleApprove(listing.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      <Check className="h-3 w-3" />
                      <span>Approuver</span>
                    </button>
                    <button
                      onClick={() => handleReject(listing.id)}
                      disabled={actionLoading}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                    >
                      <X className="h-3 w-3" />
                      <span>Rejeter</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedListing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedListing(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                 <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-gray-900">
                        {isEditing ? 'Modifier la demande' : 'Vérification de la demande'}
                    </h3>
                    {!isEditing && (
                        <button 
                        onClick={() => setIsEditing(true)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Modifier"
                        >
                        <Edit2 className="h-4 w-4" />
                        </button>
                    )}
                 </div>
                <button onClick={() => setSelectedListing(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Image Grid (Read Only) */}
              {selectedListing.images && selectedListing.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedListing.images.map((image, index) => (
                      <img key={index} src={image} alt="" className="w-full h-32 object-cover rounded-lg" />
                    ))}
                </div>
              )}

              {/* Editable Fields Grid */}
              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="text-xs text-gray-500">Marque</label>
                      {isEditing ? <input name="brand" value={editFormData.brand} onChange={handleInputChange} className="w-full border p-1 rounded" /> : <p className="font-bold">{selectedListing.brand}</p>}
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Modèle</label>
                      {isEditing ? <input name="model" value={editFormData.model} onChange={handleInputChange} className="w-full border p-1 rounded" /> : <p className="font-bold">{selectedListing.model}</p>}
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Prix</label>
                      {isEditing ? <input type="number" name="price" value={editFormData.price} onChange={handleInputChange} className="w-full border p-1 rounded" /> : <p className="font-bold text-[#D4452C]">{selectedListing.price} TND</p>}
                  </div>
                  <div>
                      <label className="text-xs text-gray-500">Année</label>
                      {isEditing ? <input type="number" name="year" value={editFormData.year} onChange={handleInputChange} className="w-full border p-1 rounded" /> : <p>{selectedListing.year}</p>}
                  </div>
              </div>
              
              <div>
                 <label className="text-xs text-gray-500">Description</label>
                 {isEditing ? 
                    <textarea name="description" value={editFormData.description} onChange={handleInputChange} className="w-full border p-2 rounded" rows={3} /> 
                    : <p className="text-sm text-gray-600">{selectedListing.description}</p>
                 }
              </div>

              {/* Footer Actions */}
              <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
                {isEditing ? (
                    <>
                        <button onClick={() => setIsEditing(false)} className="px-4 py-2 border rounded text-sm">Annuler</button>
                        <button onClick={handleUpdate} className="px-4 py-2 bg-blue-600 text-white rounded text-sm flex items-center gap-2">
                            <Save className="h-4 w-4" /> Enregistrer les modifs
                        </button>
                    </>
                ) : (
                    <>
                        <button onClick={() => handleReject(selectedListing.id)} className="px-4 py-2 bg-red-600 text-white rounded text-sm flex items-center gap-2"><X className="h-4 w-4"/> Rejeter</button>
                        <button onClick={() => handleApprove(selectedListing.id)} className="px-4 py-2 bg-green-600 text-white rounded text-sm flex items-center gap-2"><Check className="h-4 w-4"/> Approuver</button>
                    </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}